import { RichTextNode, RichTextNodeKind } from '@griply/types';

export const BULLET_MARKER = '•';
export const BOLD_MARKER = '**';
export const ITALIC_MARKER = '*';

// Markers hug the words, never the surrounding spaces — `** bold **` would not read back as
// emphasis, so the whitespace has to stay outside them.
const wrapMark = (text: string, marker: string) => {
  const trimmed = text.trim();
  if (!trimmed) {
    return text;
  }
  const leading = text.slice(0, text.length - text.trimStart().length);
  const trailing = text.slice(text.trimEnd().length);
  return `${leading}${marker}${trimmed}${marker}${trailing}`;
};

// Block boundaries travel as sentinels rather than as newlines, then resolve to the
// strongest boundary in each run: two adjacent <div>s collapse to one line break, while a
// paragraph beside anything keeps its blank line. Concatenating newlines directly cannot
// express "at least one" versus "exactly two".
// Private Use Area rather than control characters: same "cannot occur in real text"
// property, without an unmatchable NUL inside a regex.
const LINE_BOUNDARY = '\uE000';
const BLANK_BOUNDARY = '\uE001';
const BOUNDARY_RUN = new RegExp(`[${LINE_BOUNDARY}${BLANK_BOUNDARY}]+`, 'g');

const resolveBoundaries = (value: string) =>
  value.replace(BOUNDARY_RUN, (run) =>
    run.includes(BLANK_BOUNDARY) ? '\n\n' : '\n',
  );

// The visible text is what the reader wants; the href only survives alongside it when the
// text does not already show where the link goes (provider blocks render the URL itself).
const toLinkText = (href: string, label: string) => {
  if (!label) {
    return href;
  }
  const bare = href.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
  if (!bare || label.includes(bare)) {
    return label;
  }
  return `${label} (${href})`;
};

// Markers are part of the text the user edits, so a list survives the round-trip back to HTML.
const toListText = (items: RichTextNode[], ordered: boolean) =>
  items
    .filter((item) => item.kind === RichTextNodeKind.ListItem)
    .map((item, index) => {
      const marker = ordered ? `${index + 1}.` : BULLET_MARKER;
      const content =
        item.kind === RichTextNodeKind.ListItem
          ? toText(item.children).trim()
          : '';
      return `${marker} ${content}`;
    })
    .join('\n');

const toText = (nodes: RichTextNode[]): string =>
  nodes
    .map((node) => {
      switch (node.kind) {
        case RichTextNodeKind.Text:
          return node.value;
        case RichTextNodeKind.Break:
          return '\n';
        case RichTextNodeKind.Paragraph:
          return `${BLANK_BOUNDARY}${toText(node.children)}${BLANK_BOUNDARY}`;
        case RichTextNodeKind.Block:
          return `${LINE_BOUNDARY}${toText(node.children)}${LINE_BOUNDARY}`;
        case RichTextNodeKind.List:
          return `${BLANK_BOUNDARY}${toListText(
            node.children,
            node.ordered,
          )}${BLANK_BOUNDARY}`;
        case RichTextNodeKind.Link:
          return toLinkText(node.href, toText(node.children).trim());
        case RichTextNodeKind.Bold:
          return wrapMark(toText(node.children), BOLD_MARKER);
        case RichTextNodeKind.Italic:
          return wrapMark(toText(node.children), ITALIC_MARKER);
        // Underline has no marker: markdown has no underline spelling, and claiming `__`
        // would silently turn a user's `__bold__` into one. It renders, but an edit drops it.
        default:
          return toText(node.children);
      }
    })
    .join('');

export const richTextToPlainText = (nodes: RichTextNode[]): string =>
  resolveBoundaries(toText(nodes))
    .replace(/\r\n?/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
